import { useState, useEffect } from "react";
import type { NextPage } from "next";
import {
  Box,
  Typography,
  Alert,
  AlertTitle,
  Button,
  Stack,
} from "@mui/material";

import { useRouter } from "next/router";

import Head from "next/head";
import styles from "../../styles/Home.module.css";

// components
import Container from "components/container";
import QuestionHeader from "components/header";
import FormContainer from "components/controls/FormContainer";

import IntakeTextField from "components/controls/IntakeTextField";

// hooks
import { getOtp } from "hooks/useIntake";

// context
import { BookingContextType } from "utils/booking";
import { useBookingContext } from "hooks/useBooking";
import { useIntakeStore } from "@/hooks/useIntakeStore";
// =======================================================

type Otp = {
  code: string;
  failed: boolean;
  loading: boolean;
};

const MenIntake: NextPage = () => {
  const { push } = useRouter();
  const context = useBookingContext() as BookingContextType;
  const { setData } = useIntakeStore();
  const [otpOption, setOtpOption] = useState("");

  useEffect(() => {
    setData();
  }, [setData]);

  const [otp, setOtp] = useState<Otp>({
    code: "",
    failed: false,
    loading: false,
  });

  const onEmailChange = async (value: string) => {
    setData({ email: value, submitting: false });
    setOtp({
      ...otp,
      loading: true,
    });
    const { isError } = await getOtp({
      emailOrPhone: value,
      appName: "intake",
    });

    setOtp({
      ...otp,
      failed: isError,
    });
    if (!isError) push(`/verify/1`);
  };

  const invalidAccount = otp.failed && otpOption !== "";

  return (
    <div className={styles.main}>
      <Head>
        <title>Thrivelab Booking</title>
        <meta name="description" content="Book your visit" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <QuestionHeader />
      <Container
        sx={{
          display: "flex",
          flexDirection: "column",
          mt: 4,
          p: 2,
          width: { xs: "100%", lg: "25%" },
        }}
      >
        <Box
          sx={{
            width: "100%",
            mt: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            height: "100%",
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "8px",
            }}
          >
            <Typography
              variant="h1"
              align="center"
              sx={{ my: 1, fontFamily: "JazminAlt", fontSize: "24px" }}
            >
              Patient intake form
            </Typography>
            <Typography
              variant="body2"
              sx={{
                my: 3,
                fontSize: "16px",
                textAlign: "center",
                lineHeight: "22.4px",
                // minWidth: "319px",
              }}
            >
              This intake form will take 10-15 minutes and is required before
              your appointment. It will prepare your provider with details about
              your medical history, lifestyle and goals.{" "}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            background: "#f7f2e8",
            overflow: "hidden",
            border: 1,
            borderRadius: 2,
            borderColor: "#d4dacf",
            p: 2,
          }}
        >
          <FormContainer>
            {otpOption === "" && (
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontSize: "1rem",
                    my: 1,
                    fontFamily: "ABCDiaTypeRegular",
                  }}
                  textAlign="center"
                >{`Before you begin, let's verify your identity`}</Typography>
                <Typography
                  variant="h3"
                  sx={{ fontFamily: "JazminAlt", fontSize: "24px" }}
                  textAlign="center"
                >
                  How would you like to receive a one time passcode?
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  sx={{
                    mt: 2,
                    fontFamily: "ABCDiaTypeRegular",
                    textTransform: "none",
                  }}
                >
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => setOtpOption("email")}
                    sx={{
                      fontFamily: "inherit",
                      textTransform: "none",
                      background: "#3d4732",
                      height: "55px",
                    }}
                  >
                    Email
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => setOtpOption("sms")}
                    sx={{
                      fontFamily: "inherit",
                      textTransform: "none",
                      background: "#3d4732",
                      height: "55px",
                    }}
                  >
                    SMS
                  </Button>
                </Stack>
              </Box>
            )}
            {otpOption !== "" && (
              <IntakeTextField
                label={
                  otpOption === "email"
                    ? `What is your email?`
                    : "What is your mobile number?"
                }
                name={"email"}
                type={"email"}
                variant="outlined"
                containedButton
                requiredMessage={
                  otpOption === "email"
                    ? "Please provide your email"
                    : "Please provide your mobile number"
                }
                size="small"
                required
                saving={otp.loading}
                value={
                  context?.data.intake ? context?.data.intake["email"] : ""
                }
                onSelected={(value) => onEmailChange(value)}
              />
            )}
          </FormContainer>
        </Box>
        {invalidAccount && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              overflow: "none",
            }}
          >
            <Alert
              variant="standard"
              color="error"
              sx={{ mt: 2, textWrap: "wrap" }}
            >
              <AlertTitle>Account not found</AlertTitle>
              We couldn’t locate an account with the information you have
              provided. Please use your email or phone number that is on file
              with us. If you have any issues, please contact us at{" "}
              <a href="mailto:support@thrivelab.com">
                support@thrivelab.com
              </a>{" "}
              {` `}
              <a href="tel:737-377-1600">737-377-1600.</a>
            </Alert>
          </Box>
        )}{" "}
      </Container>
    </div>
  );
};

export default MenIntake;
